/**
 * User-facing copy for the app chrome.
 *
 * Every user-facing string in this workspace lives in a file like this one and
 * is imported as a constant — the copy pattern from the constitution. ESLint
 * (`react/jsx-no-literals`) fails the build on a raw string in JSX.
 *
 * **Voice** (`design/design-system/readme.md`): calm, competent, procedural.
 * Never chirpy, never salesy. Sentence case for body copy; UPPERCASE with wide
 * tracking for metadata micro-labels — though the casing is applied by
 * `<Eyebrow>` and `<Badge>`, so write these in sentence case and let the
 * components uppercase them. No emoji, ever.
 *
 * Colocate: app-level chrome here, component-owned strings in a `*.copy.ts`
 * next to the component.
 */
export const APP_COPY = {
  product: 'Blue Partner Files',

  nav: {
    label: 'Main navigation',
    /**
     * The bottom tab bar's landmark, below `sm` where the rail is gone
     * (`DomainTabBar`). Named apart from `product` (the rail's landmark) and
     * `label` (the sidebar's): in a browser only the rail or this bar is ever
     * on screen, but jsdom applies no CSS so both are in the tree in a
     * component test, and two navigation landmarks with one name make an
     * unscoped `getByRole('navigation', { name })` ambiguous — the same trap
     * `header.brand` documents for the mark.
     */
    mobileLabel: 'Sections',
    /**
     * Surfaces per SPEC.md §§4, 6, 8–10. Everything but Files is admin-only.
     *
     * **"Shared Spaces" is the administrator's index title.** Members instead
     * see `folders`: the space model is deliberately absent from their chrome
     * even though both roles share the Files rail destination (ADR-0088).
     *
     * **What changed is which noun does the removing.** "Agency Files" was
     * accurate while the surface held one grid per *agency you were connected
     * to*; a row is a room now, with a name, an owner and a roster of up to
     * about twenty-five agencies (CON-22, ADR-0066), so naming it after the
     * counterparty describes a relationship the product no longer has. The
     * rail label stays short because an icon rail has no room for a qualifier
     * and no ambiguity to resolve — it is a destination, not a description of
     * what is in it.
     */
    files: 'Shared Spaces',
    folders: 'Folders',
    foldersLoading: 'Loading folders…',
    /**
     * The Settings sidebar's two group headings (ADR-0064). They name **who a
     * setting is about**, which is the only division that survives the role
     * question: the account group is every reader's, the agency group is an
     * admin's, and a member simply never sees the second heading.
     *
     * "Agency" rather than "Admin": the heading labels the subject, not the
     * permission. A member who is shown nothing under it learns nothing from
     * being told the word "admin", and an admin already knows.
     */
    groups: {
      account: 'Your account',
      admin: 'Agency',
    },
    /**
     * The account group's three entries. `passkeys` and `signature` repeat
     * `PASSKEYS_COPY.title` and `SIGNATURE_COPY.title` **verbatim, on purpose**
     * — the account menu has linked both by those names since M9, and a sidebar
     * calling the same page something shorter would be two names for one
     * destination. If either page is renamed, rename it here too.
     */
    general: 'General',
    passkeys: 'Secure sign-in',
    signature: 'Your signature',
    scanners: 'Scanners',
    /**
     * Where an agency's connections are made and ended (CON-6, CON-14). Plural
     * and plain: it names the things listed, the same call `Users` makes.
     */
    spaces: 'Folders',
    users: 'Users',
    storage: 'Storage',
    audit: 'Audit',
    /**
     * `SurfaceNav`'s wordmark for the Settings domain, styled like `files`
     * above it in `FilesNav` — the same brand-mark treatment one level down
     * from the rail icon, so switching domains reads as switching into an
     * equally-named place rather than into an unlabelled list.
     */
    settings: 'Settings',
  },

  rail: {
    /**
     * The mark at the top of the rail. A plain `<a href="/">` in `App.tsx`,
     * not a `Link` — it forces a full navigation back to the sign-in landing
     * page, which is `Bootstrap`'s territory and has no route inside this
     * router. The name says where it goes, not what it is; nothing else on
     * the rail is called "Blue Partner Files".
     */
    brand: 'Return to the sign-in landing page',
    /** Domain switcher icons — see `domainOf`/`surfacesForDomain`. */
    files: 'Files',
    settings: 'Settings',
    navToggle: 'Toggle sidebar',
  },

  header: {
    account: 'Account',
    /**
     * The account menu's one navigational item, and the door to the settings
     * *index* — the list of settings pages — for a reader whose sidebar is
     * hidden (below 768px). The Settings *domain* has its own doors at every
     * width (the rail's gear, and `DomainTabBar` below 640px), but those land
     * on General; this one lands on the list.
     *
     * **The domain's word, not a page's.** ADR-0064 struck "Secure sign-in" and
     * "Your signature" from this menu because two named doors to one page is
     * two places to keep in agreement. Naming the *domain* costs nothing of the
     * sort: `/settings` renders `settingsGroups()`, the same array the sidebar
     * renders, so this link cannot call a page anything at all.
     *
     * Identical to `nav.settings` and `rail.settings`, deliberately — three
     * ways into one place, and a place with three names is a place nobody can
     * refer to.
     *
     * **The cost is a query trap, and it is the one `header.brand` below spent
     * a paragraph avoiding — taken here rather than avoided.** In a browser
     * only one of the three is ever reachable at a time: the rail's gear and
     * the sidebar's wordmark are for a reader who can see the nav, and this
     * link is for the one who cannot. jsdom applies no CSS, so all three are in
     * the tree in every component test. `getByRole('link', { name })` is
     * therefore ambiguous, and a query for any of them **must** be scoped to
     * its landmark — `within(getByRole('banner'))` for this one. The brand mark
     * could take a longer name because nothing reads it aloud twice; renaming
     * this one would put a second word for "Settings" in the product, which is
     * the worse trade.
     */
    settings: 'Settings',
    /**
     * The mark in the header, beside the back chevron, and **only below `sm`**.
     * The rail is `hidden … sm:flex`, so under 640px `rail.brand` above is not
     * on the screen at all and there is no mark and no way home anywhere in
     * the signed-in app. This is the same control in the one place the rail
     * cannot be.
     *
     * **Named differently from `rail.brand` on purpose, not by accident.** They
     * go to the same place, and in a browser only one of them is ever
     * displayed — but jsdom applies no CSS, so both are in the tree in every
     * component test, and two links with one name make `getByRole('link', {
     * name })` ambiguous for whoever writes the next unscoped query. Naming the
     * mark as well as the destination costs a few words and removes the trap.
     */
    brand: 'Blue Partner Files, return to the sign-in landing page',
    signOut: 'Sign out',
    /**
     * Signing out revokes the session row over the network before it navigates,
     * so there is a real wait here and the button is the only thing that can
     * report it — `AccountMenu`'s `signingOut` says why it used to report
     * nothing.
     */
    signingOut: 'Signing out…',
  },

  status: {
    loading: 'Loading…',
    empty: 'Nothing here yet.',
    error: 'Something went wrong.',
    retry: 'Try again',
    adminOnly: 'This surface is available to agency admins.',
  },

  session: {
    loading: 'Loading your account…',
    /** Transient. Retrying is worth offering. */
    unavailable: 'Could not load your account.',
    /**
     * A 403 is not a failure to be retried — the account authenticated fine and
     * simply is not set up here. Saying so, and naming who can fix it, is the
     * whole job of this string.
     */
    notProvisioned: 'This account is not set up for Blue Partner Files. Contact your agency admin.',
    retry: 'Try again',
  },

  /**
   * The one place the app admits it is not the real thing.
   *
   * It replaced two smaller signals — a "Running against mocks" chip and the
   * health card's `Storage ok · Mocked true` pair — which between them told an
   * operator that the system was unfinished without telling them the one fact
   * that matters, which is that nothing they upload here is protected. The
   * banner names the consequence instead of the mechanism.
   */
  environment: {
    testBanner: 'Test environment. Do not upload real CJI or evidence.',
    /**
     * `MockPersonaSwitch`'s label and options — mock-only, alongside the
     * banner above. "Officer" rather than "member": that is the role's name
     * on the wire, but a deputy is who this reads as in the room the product
     * is for.
     */
    viewAsLabel: 'View as',
    personas: {
      member: 'Officer',
      admin: 'Agency admin',
      /**
       * The third option, and the only one that is a different **agency**
       * rather than a different role — so it is named after the organisation
       * instead of after the job. "Prosecutor" would read as a fourth kind of
       * person at the sheriff's department, which is the one thing this option
       * is not.
       *
       * It exists for CON-24: an `own`-scope member and an `all`-scope one
       * looking at the same folder is a fact about two readers, and this is the
       * second of them.
       */
      prosecutor: 'District Attorney',
    },
    /**
     * `MockPlanSwitch`'s label and options — the second mock-only axis, and
     * PLN-1's two plans. "Docs user" and "Evidence user" rather than the wire's
     * `Docs`/`DocsEvidence`: this switch answers "what does this person see",
     * and `DocsEvidence` is a field value, not a description of anybody.
     */
    subscriptionLabel: 'Subscription',
    plans: {
      docs: 'Docs user',
      evidence: 'Evidence user',
    },
  },
} as const;
